import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { ShareddtaService } from '@app/services/shareddta.service';
import { AnalyticsService } from '@app/services/analytics.service';
import { CommonModule } from '@angular/common';
import { Chart, registerables } from 'chart.js';
import { AnalyticsResponse } from '@app/models/analytics.model';
import { BreadcrumbService } from '@app/services/breadcrumb.service';
import { Router } from '@angular/router';

Chart.register(...registerables);

interface KpiCard {
  icon: string;
  iconType: 'success' | 'warning' | 'info' | 'danger' | 'purple';
  label: string;
  value: string;
  trend: number;
  isPositive: boolean;
  comparison: string;
}

interface Driver {
  rank: number;
  name: string;
  deliveries: number;
  successRate: number;
  avgTime: number;
  distance: number;
  rating: number;
}

@Component({
  selector: 'app-analytics-dashboard',
  templateUrl: './analytics-dashboard.component.html',
  styleUrls: ['./analytics-dashboard.component.css']
})
export class AnalyticsDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('ordersChart') ordersChart!: ElementRef<HTMLCanvasElement>;
  @ViewChild('statusPieChart') statusPieChart!: ElementRef<HTMLCanvasElement>;
  @ViewChild('deliveryAreaBarChart') deliveryAreaBarChart!: ElementRef<HTMLCanvasElement>;
  @ViewChild('performanceDoughnutChart') performanceDoughnutChart!: ElementRef<HTMLCanvasElement>;
  @ViewChild('hourlyPerformanceChart') hourlyPerformanceChart!: ElementRef<HTMLCanvasElement>;
  @ViewChild('deliveryTimeDistChart') deliveryTimeDistChart!: ElementRef<HTMLCanvasElement>;
  @ViewChild('volumeForecastChart') volumeForecastChart!: ElementRef<HTMLCanvasElement>;

  private chartInstances: Chart[] = [];
  private lastResponseData: any;
  private dialogSub!: Subscription;

  selectedPeriod = "Daily";
  isLoading: boolean = true;
  startDate: string = '';
  endDate: string = '';
  todayDate: Date = new Date();
  showExportDropdown: boolean = false;
  showComparison: boolean = false;

  kpiCards: KpiCard[] = [
    {
      label: 'Total Orders',
      value: '2,847',
      trend: 12.5,
      isPositive: true,
      icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
      iconType: 'info',
      comparison: 'vs last month: +358'
    },
    {
      label: 'Orders with Signature',
      value: '1,424',
      trend: 8.4,
      isPositive: true,
      icon: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z',
      iconType: 'success',
      comparison: 'vs last month: +152'
    },
    {
      label: 'Avg Orders per Driver',
      value: '59',
      trend: 6.3,
      isPositive: true,
      icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
      iconType: 'purple',
      comparison: 'vs last month: +3.5'
    },
    {
      label: 'Avg Delivery Time',
      value: '38 min',
      trend: 5.1,
      isPositive: true,
      icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
      iconType: 'warning',
      comparison: 'vs last month: -3 min'
    },
    {
      label: 'Success Rate',
      value: '94.2%',
      trend: 3.8,
      isPositive: true,
      icon: 'M9 12l2 2 4-4M7.835 4.697a.747.747 0 001.03-1.03l4.805 4.805a.747.747 0 001.03 0l4.805-4.805a.747.747 0 001.03 1.03l-4.805 4.805a.747.747 0 000 1.03l4.805 4.805a.747.747 0 00-1.03 1.03l-4.805-4.805a.747.747 0 00-1.03 0L8.865 17.33a.747.747 0 00-1.03-1.03l4.805-4.805a.747.747 0 000-1.03L7.835 4.697z',
      iconType: 'success',
      comparison: 'vs last month: +3.5%'
    },
    {
      label: 'Active Drivers',
      value: '48',
      trend: 2.3,
      isPositive: false,
      icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
      iconType: 'info',
      comparison: 'vs last month: -1'
    }
  ];

  drivers: Driver[] = [
    { rank: 1, name: 'Tony Rodriguez', deliveries: 347, successRate: 98.5, avgTime: 32, distance: 2847, rating: 4.9 },
    { rank: 2, name: 'Anussh Mehta', deliveries: 321, successRate: 97.2, avgTime: 35, distance: 2678, rating: 4.8 },
    { rank: 3, name: 'John Kennedy', deliveries: 298, successRate: 96.8, avgTime: 38, distance: 2456, rating: 4.7 },
    { rank: 4, name: 'Maria Santos', deliveries: 276, successRate: 95.3, avgTime: 36, distance: 2234, rating: 4.8 },
    { rank: 5, name: 'David Chen', deliveries: 254, successRate: 94.7, avgTime: 39, distance: 2089, rating: 4.6 },
    { rank: 6, name: 'Lisa Anderson', deliveries: 242, successRate: 96.1, avgTime: 37, distance: 1967, rating: 4.7 },
    { rank: 7, name: 'James Taylor', deliveries: 228, successRate: 93.8, avgTime: 41, distance: 1845, rating: 4.5 },
    { rank: 8, name: 'Emma Wilson', deliveries: 215, successRate: 95.6, avgTime: 36, distance: 1734, rating: 4.6 }
  ];

  constructor(private sharedData: ShareddtaService, private analyticsService: AnalyticsService, private breadcrumbService: BreadcrumbService, private router: Router) { }

  ngOnInit(): void {
    this.breadcrumbService.set([
      { label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', action: () => this.router.navigate(['/admin/dashboard']) },
      { label: 'Analytics' }
    ]);
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setDate(today.getDate() - 30);

    this.endDate = today.toISOString().split('T')[0];
    this.startDate = lastMonth.toISOString().split('T')[0];

    this.isLoading = true;
    this.fetchAnalyticsData();

    // Listen for global dialog opens to prevent Chart.js ResizeObserver freeze
    // By destroying charts before CDK applies `position: fixed` to body, we 
    // bypass the catastrophic reflow trigger. Recreate them when dialog closes.
    this.dialogSub = this.sharedData.dialogState$.subscribe(isOpen => {
      if (isOpen) {
        this.chartInstances.forEach(chart => chart.destroy());
        this.chartInstances = [];
      } else {
        // Dialog closed: redraw instantly from cache
        if (this.lastResponseData) {
          this.initCharts(this.lastResponseData);
        } else {
          this.initCharts();
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.dialogSub) {
      this.dialogSub.unsubscribe();
    }
    this.chartInstances.forEach(chart => chart.destroy());
    this.chartInstances = [];
  }

  fetchAnalyticsData(): void {
    if (!this.startDate || !this.endDate) return;

    this.isLoading = true;
    this.analyticsService.getAnalyticsView(this.startDate, this.endDate).subscribe({
      next: (response: AnalyticsResponse) => {
        if (response && response.resultCode === '1') {
          console.log('Analytics data received:', response);
          this.updateDashboard(response);
        } else {
          this.isLoading = false;
        }
      },
      error: (error) => {
        console.error("Error fetching analytics data:", error);
        this.isLoading = false;
        this.initCharts(); // Fallback to static/current state on error
      }
    });
  }

  updateDashboard(response: AnalyticsResponse): void {
    const data = response.result;

    // 1. Update KPI Cards
    this.updateKpis(data.summary_metrics);

    // 2. Update Drivers Table
    this.drivers = data.top_performing_drivers.map(d => ({
      rank: d.rank,
      name: d.driver_name,
      deliveries: d.deliveries,
      successRate: d.success_rate,
      avgTime: parseInt(d.avg_time),
      distance: parseInt(d.total_distance),
      rating: d.rating
    }));

    // 3. Re-initialize Charts with Live Data
    this.lastResponseData = data;
    
    // Clear loading state first to show canvases
    this.isLoading = false;
    
    // Wait for next tick to ensure DOM is updated and canvases are available
    setTimeout(() => { 
      this.initCharts(data);
    }, 0);
  }

  updateKpis(metrics: any): void {
    const mapping: any = {
      'Total Orders': metrics.total_orders,
      'Orders with Signature': metrics.orders_with_signature,
      'Avg Orders per Driver': metrics.avg_orders_per_driver,
      'Avg Delivery Time': metrics.avg_delivery_time,
      'Success Rate': metrics.success_rate,
      'Active Drivers': metrics.active_drivers
    };

    this.kpiCards.forEach(card => {
      const metric = mapping[card.label];
      if (metric) {
        card.value = metric.value + (metric.unit ? ' ' + metric.unit : '');
        card.trend = metric.change;
        card.isPositive = metric.change >= 0;
        card.comparison = `vs last period: ${metric.previous}`;
      }
    });
  }

  ngAfterViewInit(): void {
    this.sharedData.signalPageReady();
    // Charts will be initialized by fetchAnalyticsData() call in ngOnInit
  }



  selectPeriod(period: string): void {
    this.selectedPeriod = period;
    if (this.lastResponseData) {
      this.initCharts(this.lastResponseData);
    }
  }

  toggleExportDropdown(): void {
    this.showExportDropdown = !this.showExportDropdown;
  }

  toggleComparison(): void {
    this.showComparison = !this.showComparison;
    this.initCharts(this.lastResponseData);
  }

  private getAggregatedTrendData(ordersTrend: any[]): { labels: string[], values: number[] } {
    let labels: string[] = [];
    let values: number[] = [];

    if (this.selectedPeriod === 'Daily') {
      labels = ordersTrend.map((d: any) => d.date);
      values = ordersTrend.map((d: any) => d.count);
    } else if (this.selectedPeriod === 'Weekly') {
      const weeklyData: { [key: string]: number } = {};
      ordersTrend.forEach((d: any) => {
        const date = new Date(d.date);
        const firstDay = new Date(date.setDate(date.getDate() - date.getDay()));
        const weekKey = firstDay.toISOString().split('T')[0];
        weeklyData[weekKey] = (weeklyData[weekKey] || 0) + d.count;
      });
      labels = Object.keys(weeklyData).sort();
      values = labels.map(label => weeklyData[label]);
    } else if (this.selectedPeriod === 'Monthly') {
      const monthlyData: { [key: string]: number } = {};
      ordersTrend.forEach((d: any) => {
        const monthKey = d.date.substring(0, 7); // YYYY-MM
        monthlyData[monthKey] = (monthlyData[monthKey] || 0) + d.count;
      });
      labels = Object.keys(monthlyData).sort();
      values = labels.map(label => monthlyData[label]);
    }
    return { labels, values };
  }

  exportReport(type: 'pdf' | 'csv-drivers' | 'csv-orders'): void {
    this.showExportDropdown = false;

    if (type === 'pdf') {
      // Use window.print() for high-quality dashboard PDF
      setTimeout(() => {
        window.print();
      }, 500);
    } else if (type === 'csv-drivers') {
      this.exportDriversCSV();
    } else if (type === 'csv-orders') {
      this.exportOrdersTrendCSV();
    }
  }

  private exportDriversCSV(): void {
    if (!this.drivers || this.drivers.length === 0) return;

    const headers = ['Rank', 'Driver Name', 'Completed Deliveries', 'Success Rate (%)', 'Avg Time (min)', 'Total Distance (km)', 'Rating'];
    const rows = this.drivers.map(d => [
      d.rank,
      d.name,
      d.deliveries,
      d.successRate,
      d.avgTime,
      d.distance,
      d.rating
    ]);

    this.generateCSV(`Driver-Performance-${this.startDate}-to-${this.endDate}.csv`, headers, rows);
  }

  private exportOrdersTrendCSV(): void {
    if (!this.lastResponseData || !this.lastResponseData.orders_trend) return;

    // Export raw daily data for trend analysis
    const headers = ['Date', 'Orders Count'];
    const rows = this.lastResponseData.orders_trend.map((d: any) => [d.date, d.count]);

    this.generateCSV(`Order-Trend-Metrics-${this.startDate}-to-${this.endDate}.csv`, headers, rows);
  }

  private generateCSV(filename: string, headers: string[], rows: any[][]): void {
    // Generate CSV content with proper escaping
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(val => `"${val}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  initCharts(liveData?: any): void {
    // If no data provided yet (startup), return
    if (!liveData && !this.ordersChart) return;

    // Destroy existing charts to prevent memory leaks and visual bugs
    this.chartInstances.forEach(chart => chart.destroy());
    this.chartInstances = [];

    // 📈 Orders Trend Line Chart
    let trendLabels: string[] = [];
    let trendValues: number[] = [];
    let prevTrendValues: number[] = [];

    if (liveData && liveData.orders_trend) {
      const aggregated = this.getAggregatedTrendData(liveData.orders_trend);
      trendLabels = aggregated.labels;
      trendValues = aggregated.values;

      if (this.showComparison) {
        // Mocking previous period data for visual comparison
        prevTrendValues = trendValues.map(v => Math.round(v * (0.85 + Math.random() * 0.3)));
      }
    }

    const datasets: any[] = [{
      label: 'Current Period',
      data: trendValues,
      borderColor: '#00a862',
      backgroundColor: 'rgba(0, 168, 98, 0.1)',
      borderWidth: 3,
      fill: true,
      tension: 0.4,
      pointRadius: 3,
      pointHoverRadius: 5,
      pointBackgroundColor: '#00a862',
      pointBorderColor: '#fff',
      pointBorderWidth: 2
    }];

    if (this.showComparison) {
      datasets.push({
        label: 'Previous Period',
        data: prevTrendValues,
        borderColor: '#94a3b8',
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [5, 5],
        fill: false,
        tension: 0.4,
        pointRadius: 2,
        pointHoverRadius: 4,
        pointBackgroundColor: '#94a3b8',
        pointBorderColor: '#fff',
        pointBorderWidth: 1
      });
    }

    const lineChart = new Chart(this.ordersChart.nativeElement, {
      type: 'line',
      data: {
        labels: trendLabels,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 500,
        animation: false,
        plugins: {
          legend: {
            display: this.showComparison,
            position: 'top',
            align: 'end',
            labels: { usePointStyle: true, font: { size: 10 } }
          },
          tooltip: {
            backgroundColor: '#0a0e14',
            padding: 12,
            titleColor: '#fff',
            bodyColor: '#b8c5b8',
            borderColor: '#2a3440',
            borderWidth: 1,
            displayColors: true,
            callbacks: {
              label: (context) => context.dataset.label + ': ' + context.parsed.y + ' orders'
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: '#f3f4f6' },
            border: { display: false },
            ticks: { color: '#6b7280', font: { size: 11 } }
          },
          x: {
            grid: { display: false },
            ticks: {
              color: '#6b7280',
              font: { size: 10 },
              maxRotation: 45,
              minRotation: 45,
              autoSkip: true,
              maxTicksLimit: 15
            }
          }
        }
      }
    });
    this.chartInstances.push(lineChart);

    // 🥧 Order Status Pie Chart
    const pieLabels = liveData ? liveData.order_status_distribution.map((d: any) => d.status) : [];
    const pieValues = liveData ? liveData.order_status_distribution.map((d: any) => d.count) : [];

    // Map labels to listing status colors
    const statusColorMap: any = {
      'successful': '#10b981',
      'unsuccessful': '#dd5d5d',
      'cancelled': '#6495ED',
      'pending': '#f59e0b',
      'en-route': '#065f46',
      'arrived': '#065f46',
      'shipped': '#0ea5e9'
    };
    const pieColors = pieLabels.map((label: string) => statusColorMap[label.toLowerCase()] || '#94a3b8');

    const pieChart = new Chart(this.statusPieChart.nativeElement, {
      type: 'pie',
      data: {
        labels: pieLabels.map((l: string) => l.charAt(0).toUpperCase() + l.slice(1)),
        datasets: [{
          data: pieValues,
          backgroundColor: pieColors,
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 500,
        animation: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: { padding: 15, usePointStyle: true, font: { size: 12 } }
          },
          tooltip: {
            backgroundColor: '#0a0e14',
            padding: 12,
            titleColor: '#fff',
            bodyColor: '#b8c5b8',
            borderColor: '#2a3440',
            borderWidth: 1
          }
        }
      }
    });
    this.chartInstances.push(pieChart);

    // 📊 Delivery Volume by Area Bar Chart
    const areaLabels = liveData ? liveData.delivery_volume_by_area.map((d: any) => d.zone) : [];
    const areaValues = liveData ? liveData.delivery_volume_by_area.map((d: any) => d.count) : [];

    const barChart = new Chart(this.deliveryAreaBarChart.nativeElement, {
      type: 'bar',
      data: {
        labels: areaLabels,
        datasets: [{
          label: 'Orders',
          data: areaValues,
          backgroundColor: ['#00a862', '#3b82f6', '#7c3aed', '#ffb347', '#10b981', '#6b7280'],
          borderRadius: 8,
          borderSkipped: false
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 500,
        animation: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#0a0e14',
            padding: 12,
            titleColor: '#fff',
            bodyColor: '#b8c5b8',
            borderColor: '#2a3440',
            borderWidth: 1,
            displayColors: false,
            callbacks: {
              label: (context) => context.parsed.y + ' orders'
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: '#f3f4f6' },
            border: { display: false },
            ticks: { color: '#6b7280', font: { size: 11 } }
          },
          x: {
            grid: { display: false },
            ticks: { color: '#6b7280', font: { size: 11 } }
          }
        }
      }
    });
    this.chartInstances.push(barChart);

    // 🍩 Delivery Performance Doughnut Chart
    let perfLabels: string[] = [];
    let perfValues: number[] = [];

    if (liveData && liveData.delivery_performance) {
      perfLabels = ['Early', 'On Time', 'Delayed'];
      perfValues = [
        liveData.delivery_performance.early.count,
        liveData.delivery_performance.on_time.count,
        liveData.delivery_performance.delayed.count
      ];
    }

    const doughnutChart = new Chart(this.performanceDoughnutChart.nativeElement, {
      type: 'doughnut',
      data: {
        labels: perfLabels,
        datasets: [{
          data: perfValues,
          backgroundColor: ['#3b82f6', '#10b981', '#dd5d5d'],
          borderWidth: 0
        }]
      },
      options: {
        cutout: '70%',
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 500,
        animation: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: { padding: 15, usePointStyle: true, font: { size: 12 } }
          },
          tooltip: {
            backgroundColor: '#0a0e14',
            padding: 12,
            titleColor: '#fff',
            bodyColor: '#b8c5b8',
            borderColor: '#2a3440',
            borderWidth: 1
          }
        }
      }
    });
    this.chartInstances.push(doughnutChart);

    // 🕒 Peak Performance Hourly Chart
    let hourlyLabels: string[] = ['6am', '8am', '10am', '12pm', '2pm', '4pm', '6pm', '8pm', '10pm'];
    let hourlyValues: number[] = [];
    const hasLiveData = !!liveData;
    const totalOrders = liveData?.summary_metrics?.total_orders?.value ?
      parseInt(liveData.summary_metrics.total_orders.value.toString().replace(/,/g, '')) : (hasLiveData ? 0 : 42);

    if (liveData && liveData.hourly_distribution) {
      hourlyLabels = liveData.hourly_distribution.map((d: any) => d.hour);
      hourlyValues = liveData.hourly_distribution.map((d: any) => d.count);
    } else {
      // Dynamic fallback scaling based on actual totalOrders
      const curve = [0.05, 0.1, 0.2, 0.25, 0.15, 0.1, 0.08, 0.05, 0.02]; // Weightings for different hours
      hourlyValues = curve.map(weight => Math.round(totalOrders * weight));
    }

    const hourlyChart = new Chart(this.hourlyPerformanceChart.nativeElement, {
      type: 'bar',
      data: {
        labels: hourlyLabels,
        datasets: [{
          label: 'Orders',
          data: hourlyValues,
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          hoverBackgroundColor: '#3b82f6',
          borderRadius: 6,
          borderSkipped: false
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 500,
        animation: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#0a0e14',
            padding: 12,
            displayColors: false,
            callbacks: {
              label: (context) => context.parsed.y + ' orders peak'
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: '#f3f4f6', drawTicks: false },
            border: { display: false },
            ticks: { color: '#6b7280', font: { size: 10 } }
          },
          x: {
            grid: { display: false },
            ticks: { color: '#6b7280', font: { size: 11 } }
          }
        }
      }
    });
    this.chartInstances.push(hourlyChart);

    // ⏱️ Delivery Time Distribution (Histogram)
    const distLabels = ['< 15m', '15-30m', '30-60m', '> 60m'];
    let distValues: number[] = [];

    if (liveData && liveData.delivery_time_distribution) {
      distValues = [
        liveData.delivery_time_distribution.under_15 || 0,
        liveData.delivery_time_distribution.under_30 || 0,
        liveData.delivery_time_distribution.under_60 || 0,
        liveData.delivery_time_distribution.over_60 || 0
      ];
    } else {
      // Dynamic fallback scaling based on actual totalOrders
      const p1 = Math.round(totalOrders * 0.35);
      const p2 = Math.round(totalOrders * 0.40);
      const p3 = Math.round(totalOrders * 0.20);
      const p4 = Math.max(0, totalOrders - p1 - p2 - p3);
      distValues = [p1, p2, p3, p4];
    }

    const distChart = new Chart(this.deliveryTimeDistChart.nativeElement, {
      type: 'bar',
      data: {
        labels: distLabels,
        datasets: [{
          data: distValues,
          backgroundColor: [
            '#10b981', // Fast (Green)
            '#3b82f6', // Standard (Blue)
            '#f59e0b', // Slow (Amber)
            '#dd5d5d'  // Outliers (Red)
          ],
          borderRadius: 8,
          borderSkipped: false
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 500,
        animation: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#0a0e14',
            padding: 12,
            displayColors: false,
            callbacks: {
              label: (context) => context.parsed.y + ' orders'
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: '#f3f4f6', drawTicks: false },
            border: { display: false },
            ticks: { color: '#6b7280', font: { size: 10 } }
          },
          x: {
            grid: { display: false },
            ticks: { color: '#6b7280', font: { size: 11 } }
          }
        }
      }
    });
    this.chartInstances.push(distChart);

    // 🔮 Volume Forecasting (Smart Projection)
    let forecastLabels: string[] = [];
    let forecastValues: number[] = [];
    let upperValues: number[] = [];
    let lowerValues: number[] = [];

    if (liveData && liveData.orders_trend && liveData.orders_trend.length > 3) {
      const data = liveData.orders_trend.map((d: any) => d.count);
      const n = data.length;
      
      // Simple Linear Regression calculation
      let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
      for (let i = 0; i < n; i++) {
        sumX += i;
        sumY += data[i];
        sumXY += i * data[i];
        sumXX += i * i;
      }
      
      const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
      const intercept = (sumY - slope * sumX) / n;

      // Forecast next 7 days
      const lastDate = new Date(liveData.orders_trend[n - 1].date);
      for (let i = 1; i <= 7; i++) {
        const nextDate = new Date(lastDate);
        nextDate.setDate(lastDate.getDate() + i);
        forecastLabels.push(nextDate.toISOString().split('T')[0]);
        
        const predicted = Math.max(0, Math.round(slope * (n + i - 1) + intercept));
        forecastValues.push(predicted);
        
        // Confidence bands (+/- 15% variance)
        upperValues.push(Math.round(predicted * 1.15));
        lowerValues.push(Math.max(0, Math.round(predicted * 0.85)));
      }
    } else {
      // Mock forecast if data is insufficient
      forecastLabels = ['Next Day', 'Day 2', 'Day 3', 'Day 4', 'Day 5', 'Day 6', 'Day 7'];
      forecastValues = [85, 92, 88, 105, 112, 108, 125];
      upperValues = forecastValues.map(v => Math.round(v * 1.15));
      lowerValues = forecastValues.map(v => Math.round(v * 0.85));
    }

    const forecastChart = new Chart(this.volumeForecastChart.nativeElement, {
      type: 'line',
      data: {
        labels: forecastLabels,
        datasets: [
          {
            label: 'Projected Volume',
            data: forecastValues,
            borderColor: '#7c3aed', // Purple
            backgroundColor: 'rgba(124, 58, 237, 0.1)',
            borderWidth: 3,
            tension: 0.4,
            fill: false
          },
          {
            label: 'High Estimate',
            data: upperValues,
            borderColor: 'rgba(124, 58, 237, 0.2)',
            backgroundColor: 'rgba(124, 58, 237, 0.05)',
            borderWidth: 1,
            pointRadius: 0,
            fill: '+1',
            tension: 0.4
          },
          {
            label: 'Low Estimate',
            data: lowerValues,
            borderColor: 'rgba(124, 58, 237, 0.2)',
            backgroundColor: 'transparent',
            borderWidth: 1,
            pointRadius: 0,
            fill: false,
            tension: 0.4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 500,
        animation: false,
        plugins: {
          legend: { position: 'top', align: 'end', labels: { boxWidth: 12, font: { size: 10 } } },
          tooltip: {
            backgroundColor: '#0a0e14',
            padding: 12,
            callbacks: {
              label: (context) => context.dataset.label + ': ' + context.parsed.y + ' orders'
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: '#f3f4f6' },
            ticks: { color: '#6b7280', font: { size: 10 } }
          },
          x: {
            grid: { display: false },
            ticks: { color: '#6b7280', font: { size: 10 } }
          }
        }
      }
    });
    this.chartInstances.push(forecastChart);
  }
}
